import { PaymentMethod } from '@aaa-ncnu-ie/mypolicy-api-billing';
import {
  PolicyTypeCd,
  PostPaymentRequest,
  PostPaymentResponse,
} from '@aaa-ncnu-ie/web-mypolicy-api-billing';
import { parse, startOfMonth, format, isValid } from 'date-fns';

import axios from './axios';

export interface SubmitPaymentDetails {
  amount: string;
  zipCode: string;
  expirationDate: string;
  email?: string;
  paymentAccountToken: string;
  policyNumber: string;
  dataSource: string;
  policyTypeCd: string;
  riskState: string;
  underwritingCompany: string;
  paymentMethod: PaymentMethod;
}

export interface PaymentItem {
  email?: string;
  paymentAccountToken: string;
  zipCode: string;
  expirationDate: string;
  paymentMethod: string;
}

export interface LineItem {
  amount: number;
  policyNumber: string;
  dataSource: string;
  policyTypeCd: PolicyTypeCd;
  riskState: string;
  underwritingCompany: string;
}

export interface SubmitMultiPolicyPayReq {
  totalAmount: number;
  paymentObj: PaymentItem;
  lineItems: LineItem[];
}

const processPayment = async (requestBody: PostPaymentRequest) => {
  const { data } = await axios.post<PostPaymentResponse>(
    `${import.meta.env.VITE_MYPOLICY_BILLING_API_URL}/v1/payment`,
    requestBody,
    {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    }
  );

  return data;
};

export const submitPayment = async ({
  amount,
  email,
  paymentAccountToken,
  zipCode,
  expirationDate,
  policyNumber,
  dataSource,
  policyTypeCd,
  riskState,
  underwritingCompany,
  paymentMethod,
}: SubmitPaymentDetails): Promise<PostPaymentResponse> => {
  const requestBody: PostPaymentRequest = {
    totalAmount: parseFloat(amount),
    lineItem: [
      {
        amount: parseFloat(amount),
        policyNumber,
        dataSource,
        policyTypeCd: policyTypeCd as PolicyTypeCd,
        riskState,
        underwritingCompany,
      },
    ],
    paymentItem: [
      {
        amount: parseFloat(amount),
        email: email ?? '',
        paymentAccountToken,
        paymentMethod,
      },
    ],
  };

  if (paymentMethod !== 'EFT' && expirationDate) {
    // New cards have the format MM/yy, saved cards have the format MM/dd/yyyy
    const dateFormat = expirationDate.length === 5 ? 'MM/yy' : 'MM/dd/yyyy';
    const parsedDate = parse(expirationDate, dateFormat, new Date());
    if (isValid(parsedDate)) {
      requestBody.paymentItem[0].card = {
        zipCode,
        expirationDate: format(startOfMonth(parsedDate), 'yyyy-MM-dd'),
      };
    }
  }

  return await processPayment(requestBody);
};

export const submitMultiPolicyPayments = async (
  submitMultiPolicyPayReq: SubmitMultiPolicyPayReq
): Promise<PostPaymentResponse> => {
  const { totalAmount, paymentObj, lineItems } = submitMultiPolicyPayReq;

  const { email, paymentAccountToken, zipCode, expirationDate, paymentMethod } =
    paymentObj;

  const requestBody: PostPaymentRequest = {
    totalAmount,
    lineItem: lineItems,
    paymentItem: [
      {
        amount: totalAmount,
        email: email ?? '',
        paymentAccountToken,
        paymentMethod,
      },
    ],
  };

  if (paymentMethod !== 'EFT' && expirationDate) {
    // New cards have the format MM/yy, saved cards have the format MM/dd/yyyy
    const dateFormat = expirationDate.length === 5 ? 'MM/yy' : 'MM/dd/yyyy';
    const parsedDate = parse(expirationDate, dateFormat, new Date());
    if (isValid(parsedDate)) {
      requestBody.paymentItem[0].card = {
        zipCode,
        expirationDate: format(startOfMonth(parsedDate), 'yyyy-MM-dd'),
      };
    }
  }

  return await processPayment(requestBody);
};

export default submitPayment;
